import { documents, type DocumentStorage } from '@/shared/api/resources';

/**
 * Envio de arquivo para um documento ja exigido.
 *
 * Fica fora do painel porque duas telas enviam: a lista de documentos de qualquer
 * dono e o "Upload de documento" da ficha do tomador, que nomeia e envia de uma vez.
 */

/** Soma de verificacao do arquivo, calculada no proprio navegador. */
async function somaDoArquivo(arquivo: File): Promise<string> {
  const conteudo = await arquivo.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', conteudo);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/** Tamanho legivel, para a lista e para a mensagem de recusa. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const unidades = ['KB', 'MB', 'GB'];
  let valor = bytes / 1024;
  let indice = 0;
  while (valor >= 1024 && indice < unidades.length - 1) {
    valor /= 1024;
    indice += 1;
  }
  return `${valor.toFixed(valor >= 10 ? 0 : 1)} ${unidades[indice]}`;
}

/** Motivo da recusa do arquivo antes de enviar, ou null quando ele pode seguir. */
export function recusaDoArquivo(
  arquivo: File,
  storage: DocumentStorage,
): string | null {
  if (arquivo.size > storage.max_bytes) {
    return `O arquivo passa do limite de ${formatBytes(storage.max_bytes)} aceito pelo repositorio.`;
  }
  if (!storage.accepted_types[arquivo.type]) {
    return 'Tipo de arquivo nao aceito pelo repositorio.';
  }
  // Com a cota rigida, o envio que passaria dela e recusado antes de criar o registro
  // (R5-B27): o servidor recusaria a reserva, e o "Upload de documento" deixaria um
  // documento sem arquivo na ficha a cada tentativa. O servidor continua conferindo.
  const cota = storage.quota_bytes;
  if (
    cota !== undefined &&
    cota !== null &&
    storage.quota_soft !== true &&
    (storage.used_bytes ?? 0) + arquivo.size > cota
  ) {
    return (
      `O envio passaria da cota de disco da empresa, de ${formatBytes(cota)}: em uso ` +
      `${formatBytes(storage.used_bytes ?? 0)}, e o arquivo tem ${formatBytes(arquivo.size)}. ` +
      'A cota e definida pela administracao da plataforma.'
    );
  }
  return null;
}

/** O que o envio diz alem de ter dado certo. */
export interface ResultadoDoEnvio {
  /**
   * Aviso da cota de disco: o envio passou da cota que a administracao da plataforma
   * permite ultrapassar (P-PLAT). Nulo dentro da cota ou sem cota.
   */
  avisoDeCota: string | null;
}

/**
 * Envio em tres passos: reservar a versao, subir o arquivo para o endereco
 * assinado e confirmar. A confirmacao e o que torna a versao valida, e por isso
 * ela vem depois do arquivo chegar.
 */
export async function enviarArquivo(
  documentId: string,
  arquivo: File,
  storage: DocumentStorage,
  extras: { display_name?: string; comment?: string } = {},
): Promise<ResultadoDoEnvio> {
  const recusa = recusaDoArquivo(arquivo, storage);
  if (recusa) throw new Error(recusa);

  const checksum = await somaDoArquivo(arquivo);
  const ticket = await documents.startUpload(documentId, {
    filename: arquivo.name,
    content_type: arquivo.type,
    byte_size: arquivo.size,
    checksum,
    ...extras,
  });

  const resposta = await fetch(ticket.upload_url, {
    method: 'PUT',
    body: arquivo,
  });
  if (!resposta.ok) {
    throw new Error('O armazenamento recusou o arquivo.');
  }

  await documents.confirmUpload(documentId, ticket.version.id);
  return {
    avisoDeCota: ticket.over_quota
      ? (ticket.quota_warning ?? 'Envio acima da cota de disco.')
      : null,
  };
}
